import { Badge, Box, Checkbox, FormHelperText, Input, Stack, Tag, Text } from "@chakra-ui/react";
import { matchesResolutions, resolutionKey } from "lib/resolution";
import { pinChosen } from "lib/sort";
import { tokenFilter } from "lib/utils";
import { useState } from "react";

// The lines of the "Broadcasts of this content" form: one tick per regular broadcast.
// - broadcasts: the regular broadcasts, sorted by name, each with `label` (its name)
// - initial / ticked: Sets of broadcast ids (the ones that played the content, the ones ticked now)
export default function BroadcastTicks({ content, broadcasts, initial, ticked, saving, onToggle, changes }) {
  const [term, setTerm] = useState("")
  // With an empty search the broadcasts that played the content when the form opened come first
  // (they stay where they are while ticking, a line would jump away from the mouse otherwise)
  const matching = broadcasts.filter(b => tokenFilter(b.label, term))
  const shown = term.trim() ? matching : pinChosen(matching, b => initial.has(b.id))

  return (
    <Stack direction="column" spacing={3}>
      <Text fontSize="sm" color="gray.500">
        Tick the regular broadcasts that should play this content. A newly ticked broadcast gets it as the last item.
        Unticking removes it from that broadcast completely, every time it appears in the list.
      </Text>
      <Input size="sm" borderRadius="30px" placeholder="Search..." value={term} onChange={e => setTerm(e.target.value)} />
      <Text fontSize="sm" color="gray.500">
        Plays in {initial.size} of {broadcasts.length} regular broadcasts{changes > 0 ? `, ${changes} change(s) not saved` : ""}
      </Text>
      <Stack direction="column" spacing={1}>
        {shown.map(b => {
          const was = initial.has(b.id)
          const is = ticked.has(b.id)
          const times = (b.contentList || []).filter(id => id === content.id).length
          const limited = Array.isArray(b.resolutions) && b.resolutions.length > 0
          const mismatch = is && !was && limited && !matchesResolutions(content, b.resolutions)
          return (
            <Box key={b.id} borderWidth="1px" borderRadius="lg" px={3} py={2} bg={is !== was ? "brand.100" : (was ? "green.50" : "white")}>
              <Checkbox colorScheme="green" isChecked={is} onChange={() => onToggle(b.id)} isDisabled={saving}>
                <Text as="span" fontSize="sm" fontWeight="medium">{b.name || "(no name)"}</Text>
              </Checkbox>
              <Stack direction="row" spacing={2} mt={1} pl={6} wrap="wrap">
                {times > 0 && <Tag size="sm" colorScheme="green" variant="subtle">{times > 1 ? `x ${times}` : "in the list"}</Tag>}
                {limited && <Tag size="sm" variant="subtle">{b.resolutions.join(", ")}</Tag>}
                {mismatch && <Badge colorScheme="orange">{resolutionKey(content) || "unknown resolution"} is not one of them</Badge>}
              </Stack>
            </Box>
          )
        })}
        {shown.length === 0 && <Text fontSize="sm" color="gray.400">Nothing found</Text>}
      </Stack>
      <FormHelperText>Composite broadcasts and multiplier copies follow their regular broadcast automatically.</FormHelperText>
    </Stack>
  )
}
